import * as types from "./constants.js";
import { Update, Clear as ClearError } from "../Error/actions";
import postRequest from "Api/PostRequests";
import getRequest from "Api/GetRequests";

// Historical note: this file previously called `actions.app.loading(...)` and
// `actions.notification.SetMessage(...)`. Neither slice exists in Store/Modules
// so those calls crashed the moment any UI actually invoked these actions.
// The local `Loading(...)` thunk below already toggles this slice's isLoading,
// which is what components read via useSelector(state => state.user.isLoading),
// so we simply drop the phantom cross-slice calls.

export const Login = (form) => {
  return (dispatch) => {
    dispatch(Loading(true));

    const result = postRequest(form, "login");

    result
      .then((response) => {
        if (isEnvelopeError(response)) {
          dispatch(Update({ message: extractErrorMessage(response) }));
        } else {
          const data = response.data || {};
          dispatch({
            type: types.LOGIN,
            payload: { ...data },
          });
          if (data.token) {
            localStorage.setItem("afrika-token", data.token);
          }
          dispatch(ClearError());
        }
        dispatch(Loading(false));
      })
      .catch((error) => {
        dispatch(
          Update({
            message: (error && error.message) || "Network error. Please try again.",
          })
        );
        dispatch(Loading(false));
      });
  };
};

// Backend envelope: { status, success (INVERTED: true = error), message, errors, data }.
// Success paths carry data.token + data.user. Anything else — including validation
// failures like "email taken" — comes through with status >= 400 and a message we
// need to surface, otherwise the register form fails silently.
const isEnvelopeError = (response) =>
  !response ||
  response.status === undefined ||
  Number(response.status) >= 400 ||
  response.success === true;

const extractErrorMessage = (response) => {
  if (!response) return "Network error. Please try again.";
  if (response.message) return response.message;
  const errs = response.errors;
  if (errs && typeof errs === "object") {
    const first = Object.values(errs)[0];
    if (Array.isArray(first) && first.length) return first[0];
    if (typeof first === "string") return first;
  }
  return "Registration failed. Please try again.";
};

export const Register = (form) => {
  return (dispatch) => {
    dispatch(Loading(true));

    const result = postRequest(form, "register");

    result
      .then((response) => {
        if (isEnvelopeError(response)) {
          dispatch(Update({ message: extractErrorMessage(response) }));
        } else {
          const data = response.data || {};
          dispatch({
            type: types.REGISTER,
            payload: { ...data },
          });
          if (data.token) {
            localStorage.setItem("afrika-token", data.token);
          }
          // Clear any stale error from a previous submission so success
          // doesn't render alongside a leftover red banner.
          dispatch(ClearError());
        }
        dispatch(Loading(false));
      })
      .catch((error) => {
        dispatch(
          Update({
            message: (error && error.message) || "Network error. Please try again.",
          })
        );
        dispatch(Loading(false));
      });
  };
};

export const GetUser = (token) => {
  return (dispatch) => {
    dispatch(Loading(true));

    const result = getRequest({}, token, "user");

    result
      .then(({ data }) => {
        if (data && data.hasOwnProperty("error")) {
          dispatch(Update(data));
        } else {
          dispatch({
            type: types.LOAD_USER,
            payload: data,
          });
        }
        dispatch(Loading(false));
      })
      .catch(() => {
        dispatch(Loading(false));
      });
  };
};

export const UpdateUser = (form, token) => {
  return (dispatch) => {
    dispatch(Loading(true));

    const result = postRequest(form, "user/update", token);

    result
      .then(({ data }) => {
        if (data && data.hasOwnProperty("error")) {
          dispatch(Update(data));
        } else {
          dispatch({
            type: types.UPDATE,
            payload: data,
          });
        }
        dispatch(Loading(false));
      })
      .catch(() => {
        dispatch(Loading(false));
      });
  };
};

export const Logout = (token) => {
  return (dispatch) => {
    dispatch(Loading(true));

    const result = postRequest({}, "logout", token);

    const finish = () => {
      localStorage.removeItem("afrika-token");
      dispatch({ type: types.LOGOUT, payload: {} });
      dispatch(Loading(false));
      window.location.assign("/");
    };

    result.then(finish).catch(finish);
  };
};

export const Loading = (isLoading) => {
  return (dispatch) => {
    dispatch({
      type: types.LOADING_STATUS,
      payload: isLoading,
    });
  };
};
